import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { PredefinedMilisecondsTypeEnum } from '../../../lib/enums';
import { RootState } from '../../../lib/store';
import { addOrRemoveSymbolToFavoriteList } from '../../../lib/store/common-actions';
import { SymbolFavoriteListActionType } from '../../../lib/store/market-data/types';
import TradePriceCache from '../../../lib/store-util/TradePriceCache';
import { formatCurrency } from '../../../lib/util/DataHelpers';
import {
  calculatePriceMovement,
  roundToCurrencyFormat,
} from '../../../lib/util/lastPriceChangeHelpers';
import { PriceMovement } from '../../../lib/util/types';

import { useBrowserWidth } from '../../../hooks';
import { UI_ROUTES } from '../../../util/routes';
import { SymbolTableGrid } from '../../SymbolTable/SymbolTableGrid.enum';
import SymbolTableRow from '../../SymbolTable/SymbolTableRow/SymbolTableRow';
import { WatchListSortedStocks, WatchListStocks } from '../WatchList.types';

const WatchListRow = (props: {
  stock: WatchListStocks;
  tempDataStocks: WatchListSortedStocks[];
  updateTempDataStocks: (updatedStock: WatchListSortedStocks) => void;
  hasSwipeableRows?: boolean;
  swipedRow?: string;
  setSwipedRow?: React.Dispatch<React.SetStateAction<string>>;
  showPercentage?: boolean;
  setShowPercentage?: (value: boolean) => void;
  setRemovedFavouriteSymbol?: React.Dispatch<React.SetStateAction<string>>;
}): JSX.Element => {
  const {
    stock,
    tempDataStocks,
    updateTempDataStocks,
    hasSwipeableRows,
    swipedRow,
    setSwipedRow,
    showPercentage = true,
    setShowPercentage,
    setRemovedFavouriteSymbol,
  } = props;

  const navigate = useNavigate();
  const { browserWidthSize } = useBrowserWidth();
  const dispatch = useDispatch();

  const [removeSymbol, setRemoveSymbol] = useState<string>('');

  const companyLogosAndNames = useSelector(
    (state: RootState) => state.fundamental.companyLogoAndName,
  );
  const userCorrelationId = useSelector(
    (state: RootState) => state.crm.individualExtendedInfo?.user_correlation_id,
  );
  const userIdOrCorrelationId = userCorrelationId;

  const { logo, name, url } = companyLogosAndNames[stock?.symbol] ?? {};
  const { symbol } = stock;

  const {
    direction: directionStock,
    change: changeStock,
    changePercentWithSign: changePercentWithSignStock,
    currentPrice,
  } = TradePriceCache.use(symbol, 'WatchListTable');
  const [priceMovement, setPriceMovement] = useState<PriceMovement>('equal');
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const newMovement = calculatePriceMovement(currentPrice, symbol);
    setPriceMovement(newMovement);

    // Reset to default after 2 seconds
    if (newMovement !== 'equal') {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        setPriceMovement('equal');
      }, PredefinedMilisecondsTypeEnum.twoSeconds);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [currentPrice, symbol]);

  const change = changeStock;
  const changePercentWithSign = changePercentWithSignStock;
  const direction = directionStock;

  const tempDataStock = {
    symbol: stock.symbol,
    currentPrice: roundToCurrencyFormat(currentPrice || 0),
    change: parseFloat(change || '0'),
    changePercent: parseFloat(changePercentWithSign || '0'),
    logoBase64: logo,
    companyName: name,
    url,
  };

  // Update tempDataStocks in the parent
  useEffect(() => {
    updateTempDataStocks(tempDataStock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPrice, change, changePercentWithSign, logo, name, url]);

  useEffect(() => {
    const copiedSymbol = removeSymbol;
    setSwipedRow && setSwipedRow('');
    setRemoveSymbol('');

    if (!copiedSymbol) {
      return;
    }

    dispatch(
      addOrRemoveSymbolToFavoriteList({
        symbolAndExchange: {
          symbol: copiedSymbol,
          stockExchangeCode: stock.stockExchangeCode,
          countryDesignation: stock.countryDesignation,
        },
        actionType: SymbolFavoriteListActionType.Remove,
        userCorrelationId: userIdOrCorrelationId,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeSymbol, stock.stockExchangeCode, stock.countryDesignation, userIdOrCorrelationId]);

  const onRowSelect = () => {
    navigate(`${UI_ROUTES.symbol}/${stock.symbol}`);
  };

  const onRemoveHandler = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setRemoveSymbol(stock.symbol);
    if (setRemovedFavouriteSymbol) setRemovedFavouriteSymbol(stock.symbol);
  };

  const roundedCurrentPrice = useMemo(() => (currentPrice ?? 0).toFixed(4), [currentPrice]);

  const lastPriceFormatted = formatCurrency(roundedCurrentPrice);
  const formattedChange = useMemo(() => formatCurrency(change), [change]);

  return (
    <SymbolTableRow
      key={stock.symbol}
      id={stock.symbol}
      name={name}
      hasSwipeableRows={hasSwipeableRows}
      swipedRow={swipedRow}
      setSwipedRow={setSwipedRow}
      oneRowInfoContent={browserWidthSize < 1280}
      onRemove={onRemoveHandler}
      onRowClick={onRowSelect}
      isWithNews
      gridTemplateColumns={SymbolTableGrid.FIVE_ROW_ITEMS_GRID}
      model={{
        stock,
        chartSymbol: symbol,
        lastPrice: lastPriceFormatted,
        changePercent: changePercentWithSign,
        changePrice: direction === '+' ? `+${formattedChange}` : formattedChange,
        direction,
        onToggleShowPercent: () => setShowPercentage?.(!showPercentage),
        priceMovement,
        showPercentage,
      }}
    />
  );
};

export default WatchListRow;
